<template>
  <div class="quick-links-page px-4 py-4 min-h-screen">
    <!-- ── 1. UNIFIED PAGE HEADER (Matches Spark's Standard Design) ── -->
    <div class="ms-page-header mb-4">
      <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
        <div class="d-flex align-items-center gap-3">
          <div class="ms-page-header__icon">
            <i class="fa-solid fa-link"></i>
          </div>
          <div>
            <p class="ms-page-header__breadcrumb">Platforms Delivery <span class="ms-breadcrumb-sep">/</span> Quick Links</p>
            <h1 class="ms-page-header__title">Quick Links</h1>
          </div>
        </div>

        <!-- ── 2. PAGE NAVIGATION TABS (Organisation Dashboard Style) ── -->
        <div class="tabs-container d-flex gap-2 p-1 bg-light-inset rounded-3 shadow-xs">
          <button 
            class="tab-btn btn btn-sm px-3 py-2 fw-bold transition-all" 
            :class="activeTab === 'hub' ? 'active-tab shadow-sm text-info bg-white' : 'text-muted-custom border-0 bg-transparent hover-tab'"
            @click="activeTab = 'hub'"
          >
            <i class="fa-solid fa-shapes me-1"></i> Interactive Hub
          </button>
          <button 
            class="tab-btn btn btn-sm px-3 py-2 fw-bold transition-all" 
            :class="activeTab === 'manage' ? 'active-tab shadow-sm text-info bg-white' : 'text-muted-custom border-0 bg-transparent hover-tab'"
            @click="activeTab = 'manage'"
          >
            <i class="fa-solid fa-sliders me-1"></i> Manage Directory
          </button>
        </div>
      </div>
    </div>

    <!-- ── 3. TAB MAIN CONTENT ── -->
    <!-- TAB 1: INTERACTIVE HUB (FLUID ACCORDION CARD GRID) -->
    <div v-if="activeTab === 'hub'" class="fade-in-content">
      <!-- Search & Guidance -->
      <div class="row mb-4 align-items-center">
        <div class="col-12 col-md-8 mx-auto text-center">
          <div class="search-wrapper position-relative mb-2">
            <i class="fa-solid fa-magnifying-glass search-icon text-muted-custom"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search categories or resources (e.g. 'uat', 'jira', 'figma')..." 
              class="form-control form-control-lg bg-white text-dark border-secondary-light rounded-pill ps-5 pe-4 text-base focus-glow"
            />
          </div>
          <p class="text-muted-custom text-xs mb-0">
            <span class="badge bg-light border text-muted-custom py-1 px-2.5 me-1">Alt + Shortcut Key</span> 
            expands category instantly • Search query <strong>automatically expands</strong> all matching card accordions
          </p>
        </div>
      </div>

      <!-- Syncing Indicators -->
      <div v-if="isLoading" class="d-flex flex-column align-items-center justify-content-center py-5">
        <div class="spinner-border text-info mb-3" role="status"></div>
        <span class="text-muted-custom">Synchronizing delivery hub directory...</span>
      </div>

      <div v-else-if="filteredGroups.length === 0" class="text-center py-5">
        <div class="empty-state-icon mb-3">
          <i class="fa-solid fa-folder-open fa-3x text-muted-custom opacity-50"></i>
        </div>
        <h4 class="h5 fw-bold text-muted-custom">No resources found matching "{{ searchQuery }}"</h4>
        <p class="text-muted-custom text-sm">Try broadening your terms or define a new category in the Directory tab.</p>
      </div>

      <!-- Spacious Fluid Columns (Masonry Grid to Prevent Row Stretching) -->
      <div v-else class="row g-4 align-items-start">
        <div 
          v-for="(col, colIdx) in columnGroups" 
          :key="colIdx" 
          class="col-12 col-md-6 col-lg-4 d-flex flex-column gap-4"
        >
          <!-- Individual Group Card -->
          <div 
            v-for="group in col" 
            :key="group.id" 
            class="card border-0 shadow-sm project-card bg-white w-100 d-flex flex-column transition-all"
            :class="[
              `theme-color-${safeColor(group.color)}`,
              { 'is-expanded-card': isGroupExpanded(group) }
            ]"
            :id="`accordion-group-${group.id}`"
            @click="toggleGroup(group)"
          >
            <div class="card-body p-4 d-flex flex-column justify-content-between h-100">
              <div class="flex-grow-1">
                <!-- Card Header (Icon & Actions) -->
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <!-- Rounded Theme-colored icon box -->
                  <div class="project-icon-wrapper" :class="`${safeColor(group.color)}-theme`">
                    <i class="fa-solid fa-folder"></i>
                  </div>
                  <!-- Shortcut key badge -->
                  <span class="shortcut-tag text-muted-custom text-xxs bg-light border border-secondary-light px-2 py-0.5 rounded">
                    Alt + {{ group.shortcut || '?' }}
                  </span>
                </div>

                <!-- Title & Description -->
                <h4 class="fw-bold text-dark mb-1 card-project-title">{{ group.title }}</h4>
                <p class="text-muted small text-clamp mb-3" style="min-height: 40px; line-height: 1.45;">
                  {{ group.description || 'Collaborative configuration endpoints and environment indexes.' }}
                </p>
              </div>

              <!-- Accordion Footer & Meta -->
              <div>
                <div class="d-flex justify-content-between align-items-center pt-3 border-top border-light text-info fw-bold small card-action-link">
                  <span class="text-muted small fw-semibold text-color-label">
                    <i class="fa-solid fa-list-check me-1"></i> {{ group.links ? group.links.length : 0 }} Resources
                  </span>
                  <span class="small d-flex align-items-center gap-1">
                    {{ isGroupExpanded(group) ? 'Hide links' : 'View links' }}
                    <i class="fa-solid fa-chevron-down transition-transform chevron-spin" :class="{ rotated: isGroupExpanded(group) }"></i>
                  </span>
                </div>

                <!-- Expanded Links List (Pure Clean White background, no grey box) -->
                <transition name="slide-fade">
                  <div v-show="isGroupExpanded(group)" class="mt-3 pt-3 border-top border-light-subtle" @click.stop>
                    <div v-if="!group.links || group.links.length === 0" class="text-center py-3 text-muted-custom text-xs">
                      No links defined. Add some in the <strong>Manage Directory</strong> tab!
                    </div>
                    
                    <!-- Vertical stack of elegant, padded link items -->
                    <div v-else class="d-flex flex-column gap-2">
                      <div 
                        v-for="link in group.links" 
                        :key="link.id"
                        class="link-item-row d-flex align-items-center justify-content-between transition-all"
                        :class="{ 'glow-highlight': isSearchMatch(link.name) }"
                      >
                        <a 
                          :href="link.url" 
                          target="_blank" 
                          class="text-decoration-none d-flex align-items-center gap-2.5 truncate pe-2 w-100"
                        >
                          <i class="fa-solid fa-link text-color-label shrink-0" style="font-size: 11px;"></i>
                          <div class="truncate d-flex flex-column align-items-start">
                            <span class="text-sm fw-bold text-dark hover-underline text-truncate block mb-0.5">{{ link.name }}</span>
                            <span class="text-xxs text-muted-custom text-truncate font-monospace d-block" style="max-width: 100%; opacity: 0.85;">{{ truncateUrl(link.url) }}</span>
                          </div>
                        </a>
                        
                        <a :href="link.url" target="_blank" class="text-muted-custom shrink-0 transition-transform arrow-reveal ms-2" title="Open Link">
                          <i class="fa-solid fa-arrow-up-right-from-square text-xxs"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 2: MANAGE DIRECTORY -->
    <div v-if="activeTab === 'manage'" class="fade-in-content">
      <!-- ── TOP CONTROL BAR ── -->
      <div class="card border-0 shadow-sm p-3 rounded-xl bg-white mb-4">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <!-- Search & Filter (Left Side) -->
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <!-- Sleek Search -->
            <div class="input-group" style="max-width: 280px;">
              <span class="input-group-text bg-white border-end-0 border-light-subtle py-2 px-3" style="border-radius: 8px 0 0 8px; height: 38px;">
                <i class="fa-solid fa-magnifying-glass text-muted small"></i>
              </span>
              <input 
                type="text" 
                v-model="filterManagerSearchQuery" 
                placeholder="Search resources..." 
                class="form-control border-start-0 border-light-subtle bg-white text-dark focus-glow text-xs py-2" 
                style="border-radius: 0 8px 8px 0; height: 38px;" 
              />
            </div>

            <!-- Vertex-Style Category Filter Dropdown (Matches Leads Page filters) -->
            <div class="dropdown">
              <button 
                type="button" 
                class="btn btn-sm custom-filter-dropdown-btn fw-bold d-flex align-items-center justify-content-between px-3 shadow-xs" 
                id="categoryFilterDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
                style="height: 38px; border-radius: 8px !important; display: inline-flex; align-items: center;"
              >
                <span class="d-flex align-items-center text-dark" style="font-size: 14px;">
                  Category
                  <i class="fa-solid fa-filter ms-2" style="font-size: 12px; color: #00A0C1;"></i>
                </span>
                <span v-if="filterManagerGroupIds.length > 0" class="badge ms-2 rounded-pill text-white" style="font-size: 10px; background-color: #00A0C1 !important; padding: 3px 6px;">
                  {{ filterManagerGroupIds.length }}
                </span>
              </button>
              <div class="dropdown-menu p-3 shadow-lg border-0 dropdown-menu-start" style="min-width: 240px; border-radius: 8px;" @click.stop>
                <!-- Quick Clear / Select All utility row -->
                <div class="d-flex justify-content-between align-items-center border-bottom border-light-subtle pb-2 mb-2">
                  <span class="text-xxs text-muted-custom fw-bold uppercase-tracker">Categories Filter</span>
                  <button v-if="filterManagerGroupIds.length > 0" @click="filterManagerGroupIds = []" class="btn btn-link p-0 text-decoration-none text-xxs text-info fw-bold">
                    Clear All
                  </button>
                </div>
                <div class="d-flex flex-column gap-2.5 w-100 overflow-auto thin-scrollbar-dropdown" style="max-height: 250px;">
                  <div v-for="g in groups" :key="g.id" class="d-flex align-items-center">
                    <label class="d-flex align-items-center cursor-pointer mb-0 w-100">
                      <input 
                        class="custom-checkbox-input" 
                        type="checkbox" 
                        :id="'dir-category-' + g.id" 
                        :value="g.id" 
                        v-model="filterManagerGroupIds" 
                        style="pointer-events: auto;"
                      />
                      <span class="custom-checkbox"></span>
                      <span class="ms-2 select-none d-flex align-items-center gap-1.5" style="font-size: 13px; font-weight: 500; color: #495057;">
                        <span class="bullet-dot" :class="`bg-${safeColor(g.color)}`" style="width: 6px; height: 6px;"></span>
                        {{ g.title }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Buttons (Right Side) -->
          <div class="d-flex align-items-center gap-2">
            <button 
              class="btn btn-sm btn-directory-action fw-bold d-flex align-items-center gap-2 px-3 py-2 shadow-xs"
              data-bs-toggle="modal" 
              data-bs-target="#groupModal"
              @click="openAddGroupModal"
              style="height: 38px; border-radius: 8px; font-size: 13px;"
            >
              <i class="fa-solid fa-folder-plus text-xs"></i> Add Grouping
            </button>
            <button 
              class="btn btn-sm btn-info text-white fw-bold d-flex align-items-center gap-2 px-3 py-2 shadow-xs"
              data-bs-toggle="modal" 
              data-bs-target="#linkModal"
              @click="openAddLinkModal"
              style="height: 38px; border-radius: 8px; font-size: 13px;"
            >
              <i class="fa-solid fa-plus text-xs"></i> Add Link
            </button>
          </div>
        </div>
      </div>

      <!-- ── MAIN SPLIT DASHBOARD ── -->
      <div class="row g-4">
        <!-- LEFT PANEL: Groupings Index -->
        <div class="col-12 col-xl-5">
          <div class="card border-0 shadow-sm p-4 rounded-xl bg-white" style="min-height: 500px;">
            <div class="d-flex justify-content-between align-items-center border-bottom border-light-subtle pb-3 mb-3">
              <h2 class="h6 fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <i class="fa-solid fa-tags text-info"></i> Directory Groupings
              </h2>
              <span class="badge bg-light text-dark-emphasis fw-bold rounded-pill px-2.5 py-1 text-xxs">
                {{ groups.length }} Categories
              </span>
            </div>

            <!-- List of existing groups -->
            <div class="groups-list overflow-auto pe-1" style="max-height: 550px;">
              <div v-if="groups.length === 0" class="text-center py-5 text-muted-custom text-xs">
                No groupings created yet. Click "Add Grouping" to begin!
              </div>
              <div 
                v-for="g in groups" 
                :key="g.id" 
                class="group-row py-3 px-4 rounded-xl border border-light-subtle bg-white d-flex align-items-center justify-content-between mb-3 hover-card transition-all"
                :class="'theme-color-' + safeColor(g.color)"
              >
                <!-- Beautifully Aligned Shortcut & Text Info columns -->
                <div class="d-flex align-items-center gap-3 truncate pe-2 w-100">
                  <div class="shrink-0">
                    <span class="group-shortcut-badge" :class="`${safeColor(g.color)}-theme`">
                      {{ g.shortcut || '?' }}
                    </span>
                  </div>
                  <div class="truncate flex-grow-1 min-w-0">
                    <div class="text-sm fw-bold text-dark truncate mb-1">
                      {{ g.title }}
                    </div>
                    <div class="text-xs text-muted-custom truncate">{{ g.description || 'No description provided' }}</div>
                  </div>
                </div>

                <div class="d-flex gap-2 shrink-0 ms-2">
                  <button 
                    @click="startEditGroup(g)" 
                    class="action-btn-edit" 
                    data-bs-toggle="modal" 
                    data-bs-target="#groupModal" 
                    title="Edit Grouping"
                  >
                    <i class="fa-solid fa-pencil text-xs"></i>
                  </button>
                  <button @click="deleteGroup(g)" class="action-btn-delete" title="Delete Grouping">
                    <i class="fa-solid fa-trash-can text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT PANEL: Hyperlinks Index -->
        <div class="col-12 col-xl-7">
          <div class="card border-0 shadow-sm p-4 rounded-xl bg-white h-100" style="min-height: 500px;">
            <div class="d-flex justify-content-between align-items-center border-bottom border-light-subtle pb-3 mb-3">
              <h2 class="h6 fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <i class="fa-solid fa-link text-info"></i> Hyperlinks Directory
              </h2>
              <span class="badge bg-light text-dark-emphasis fw-bold rounded-pill px-2.5 py-1 text-xxs">
                {{ filteredManagerLinks.length }} matched
              </span>
            </div>

            <div class="links-manager-list overflow-auto pe-1" style="max-height: 550px;">
              <div v-if="filteredManagerLinks.length === 0" class="text-center py-5 text-muted-custom text-xs">
                No hyperlinks matched the current criteria. Click "Add Link" to create one!
              </div>
              <div 
                v-for="l in filteredManagerLinks" 
                :key="l.id" 
                class="link-row py-3 px-4 rounded-xl border border-light-subtle bg-white d-flex align-items-center justify-content-between mb-3 hover-card transition-all"
                :class="'theme-color-' + (l.parentColor || 'blue')"
              >
                <div class="d-flex align-items-center gap-3 truncate pe-2 w-100">
                  <div class="truncate flex-grow-1 min-w-0">
                    <div class="text-sm fw-bold text-dark d-flex align-items-center gap-2 mb-1.5 flex-wrap">
                      <span>{{ l.name }}</span>
                      <!-- Conditionally render capsule badge only if category title is valid to avoid empty pill shapes -->
                      <span v-if="getGroupTitleById(l.group_id)" class="badge border bg-white px-2 py-0.5 rounded-pill" :class="`border-${l.parentColor || 'blue'} text-${l.parentColor || 'blue'}`" style="font-size: 11px; font-weight: 600;">
                        {{ getGroupTitleById(l.group_id) }}
                      </span>
                    </div>
                    <div class="text-xs text-muted-custom text-truncate block font-monospace" style="max-width: 380px;">
                      <i class="fa-solid fa-arrow-turn-up-right text-muted-custom scale-x-n1 me-1"></i> {{ truncateUrl(l.url) }}
                    </div>
                  </div>
                </div>

                <div class="d-flex gap-2 shrink-0 ms-2">
                  <button 
                    @click="startEditLink(l)" 
                    class="action-btn-edit" 
                    data-bs-toggle="modal" 
                    data-bs-target="#linkModal" 
                    title="Edit Resource"
                  >
                    <i class="fa-solid fa-pencil text-xs"></i>
                  </button>
                  <button @click="deleteLink(l)" class="action-btn-delete" title="Delete Resource">
                    <i class="fa-solid fa-trash-can text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 4. DIRECTORY EDITING MODALS (Matches Vertex-Style Focus Flow) ── -->
    <!-- Grouping Modal -->
    <div class="modal fade" id="groupModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-custom-width">
        <div class="modal-content border-0 shadow-lg rounded-xl bg-white">
          <form @submit.prevent="saveGroup" class="modal-form-custom">
            <div class="p-4" style="position: relative;">
              <!-- BDM Details standard absolute top-right close button -->
              <button 
                ref="closeGroupModal" 
                type="button" 
                class="close-button-custom" 
                data-bs-dismiss="modal" 
                aria-label="Close"
              >&times;</button>

              <!-- Left-aligned BDM standard title and HR separator -->
              <h5 class="modal-title-custom text-dark mb-0 d-flex align-items-center gap-2">
                <i class="fa-solid fa-folder text-info"></i>
                <span>{{ editGroupId ? 'Edit Grouping Category' : 'Create New Grouping Category' }}</span>
              </h5>
              <hr class="my-3 text-muted" style="opacity: 0.15;" />

              <div class="mb-4">
                <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">Group Title <span class="text-danger">*</span></label>
                <input type="text" v-model="groupForm.title" placeholder="e.g. Production Platform Links" class="form-control bg-white text-dark border-light-subtle focus-glow" style="border-radius: 8px;" required />
              </div>

              <div class="mb-4">
                <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">Description</label>
                <textarea v-model="groupForm.description" rows="2" placeholder="Central list of live production instances..." class="form-control bg-white text-dark border-light-subtle focus-glow" style="border-radius: 8px;"></textarea>
              </div>

              <div class="row mb-4">
                <div class="col-8">
                  <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">Color Theme</label>
                  <!-- Vertex-Style Custom select dropdown -->
                  <div class="dropdown custom-select-dropdown">
                    <button 
                      class="btn btn-sm btn-light border dropdown-toggle fw-bold d-flex align-items-center justify-content-between px-3 w-100 shadow-xs text-start bg-white focus-glow text-dark" 
                      type="button" 
                      id="colorThemeDropdown" 
                      data-bs-toggle="dropdown" 
                      aria-expanded="false"
                      style="height: 42px; border-radius: 8px !important; font-size: 14px;"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="bullet-dot shrink-0" :class="`bg-${safeColor(groupForm.color)}`" style="width: 8px; height: 8px;"></span>
                        <span class="text-truncate">{{ getColorThemeLabel(groupForm.color) }}</span>
                      </span>
                    </button>
                    <ul class="dropdown-menu shadow border-0 p-1 w-100" aria-labelledby="colorThemeDropdown" style="max-height: 250px; overflow-y: auto; border-radius: 8px;">
                      <li v-for="col in colorThemesList" :key="col.value">
                        <a class="dropdown-item d-flex align-items-center gap-2 py-2 rounded-2" href="#" @click.prevent="groupForm.color = col.value" style="font-size: 13.5px;">
                          <span class="bullet-dot" :class="`bg-${col.value}`" style="width: 8px; height: 8px;"></span>
                          <span class="text-dark">{{ col.label }}</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="col-4">
                  <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">Shortcut Key <span class="text-danger">*</span></label>
                  <input type="text" v-model="groupForm.shortcut" maxlength="1" placeholder="e.g. U" class="form-control bg-white text-dark border-light-subtle text-center fw-bold uppercase-input focus-glow" style="border-radius: 8px; height: 42px;" required />
                </div>
              </div>

              <!-- Footer action buttons with clean spacing -->
              <div class="mt-4 d-flex justify-content-end align-items-center gap-2">
                <button type="button" @click="cancelEditGroup" class="btn btn-sm btn-outline-secondary border-0 bg-transparent text-muted-custom fw-bold px-3 py-2 text-xs" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-sm btn-info text-white fw-bold px-4 py-2.5 shadow-xs" :disabled="isLoading" style="border-radius: 8px; font-size: 13px;">
                  <i class="fa-solid fa-save me-1.5"></i> {{ editGroupId ? 'Save Category' : 'Create Category' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Link Modal -->
    <div class="modal fade" id="linkModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-custom-width">
        <div class="modal-content border-0 shadow-lg rounded-xl bg-white">
          <form @submit.prevent="saveLink" class="modal-form-custom">
            <div class="p-4" style="position: relative;">
              <!-- BDM Details standard absolute top-right close button -->
              <button 
                ref="closeLinkModal" 
                type="button" 
                class="close-button-custom" 
                data-bs-dismiss="modal" 
                aria-label="Close"
              >&times;</button>

              <!-- Left-aligned BDM standard title and HR separator -->
              <h5 class="modal-title-custom text-dark mb-0 d-flex align-items-center gap-2">
                <i class="fa-solid fa-link text-info"></i>
                <span>{{ editLinkId ? 'Edit Hyperlink Details' : 'Add New Hyperlink' }}</span>
              </h5>
              <hr class="my-3 text-muted" style="opacity: 0.15;" />

              <div class="mb-4">
                <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">Target Category / Grouping <span class="text-danger">*</span></label>
                <!-- Vertex-Style Custom select dropdown -->
                <div class="dropdown custom-select-dropdown">
                  <button 
                    class="btn btn-sm btn-light border dropdown-toggle fw-bold d-flex align-items-center justify-content-between px-3 w-100 shadow-xs text-start bg-white focus-glow text-dark" 
                    type="button" 
                    id="targetGroupDropdown" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    style="height: 42px; border-radius: 8px !important; font-size: 14px;"
                  >
                    <span class="text-truncate">{{ getGroupTitleById(linkForm.group_id) || 'Select category...' }}</span>
                  </button>
                  <ul class="dropdown-menu shadow border-0 p-1 w-100" aria-labelledby="targetGroupDropdown" style="max-height: 250px; overflow-y: auto; border-radius: 8px;">
                    <li v-for="g in groups" :key="g.id">
                      <a class="dropdown-item d-flex align-items-center gap-2 py-2.5 rounded-2" href="#" @click.prevent="linkForm.group_id = g.id" style="font-size: 13.5px;">
                        <span class="bullet-dot" :class="`bg-${safeColor(g.color)}`" style="width: 8px; height: 8px;"></span>
                        <span class="text-dark">{{ g.title }}</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="mb-4">
                <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">Link Name / Title <span class="text-danger">*</span></label>
                <input type="text" v-model="linkForm.name" placeholder="e.g. Auth UAT Login" class="form-control bg-white text-dark border-light-subtle focus-glow" style="border-radius: 8px;" required />
              </div>

              <div class="mb-4">
                <label class="form-label text-muted-custom text-xs fw-bold mb-1.5">URL / Endpoint <span class="text-danger">*</span></label>
                <input type="url" v-model="linkForm.url" placeholder="https://example.com" class="form-control bg-white text-dark border-light-subtle focus-glow" style="border-radius: 8px;" required />
              </div>

              <!-- Footer action buttons with clean spacing -->
              <div class="mt-4 d-flex justify-content-end align-items-center gap-2">
                <button type="button" @click="cancelEditLink" class="btn btn-sm btn-outline-secondary border-0 bg-transparent text-muted-custom fw-bold px-3 py-2 text-xs" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-sm btn-info text-white fw-bold px-4 py-2.5 shadow-xs" :disabled="isLoading" style="border-radius: 8px; font-size: 13px;">
                  <i class="fa-solid fa-plus me-1.5"></i> {{ editLinkId ? 'Save Link' : 'Add Link' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QuickLinksDashboard",
  data() {
    return {
      activeTab: "hub",
      searchQuery: "",
      isLoading: false,

      // Database-backed state arrays
      groups: [],

      // Track manual accordion toggle states
      expandedGroupsMap: {},

      // Group Form details
      editGroupId: null,
      groupForm: {
        title: "",
        description: "",
        color: "blue",
        shortcut: ""
      },

      // Link Form details
      editLinkId: null,
      linkForm: {
        group_id: "",
        name: "",
        url: ""
      },

      // Filter settings inside the manager
      filterManagerGroupIds: [],
      filterManagerSearchQuery: "",

      colorThemesList: [
        { value: "blue", label: "Blue (UAT / Dev)" },
        { value: "green", label: "Green (Production)" },
        { value: "amber", label: "Amber (Figma / Jira)" },
        { value: "cyan", label: "Cyan (Checklists)" },
        { value: "red", label: "Red (Spark / Lead Forms)" },
        { value: "purple", label: "Purple (Other Links)" },
        { value: "pink", label: "Pink (PIVs / Regression)" },
        { value: "lime", label: "Lime (Daily Tasks)" },
        { value: "teal", label: "Teal (Integrations)" },
        { value: "indigo", label: "Indigo (Monitoring)" },
        { value: "orange", label: "Orange (Backlogs)" },
        { value: "emerald", label: "Emerald (Releases)" },
        { value: "rose", label: "Rose (Hotfixes)" },
        { value: "violet", label: "Violet (APIs)" },
        { value: "slate", label: "Slate (Infrastructure)" },
        { value: "gray", label: "Gray (Technical References)" }
      ]
    };
  },

  computed: {
    // Return groups filtered based on the landing page search query
    filteredGroups() {
      const q = this.searchQuery.toLowerCase().trim();
      if (!q) return this.groups;

      return this.groups.filter(g => {
        const groupTitleMatch = g.title.toLowerCase().includes(q);
        const groupDescMatch = g.description && g.description.toLowerCase().includes(q);
        const linkMatch = g.links && g.links.some(l => l.name.toLowerCase().includes(q) || l.url.toLowerCase().includes(q));
        return groupTitleMatch || groupDescMatch || linkMatch;
      });
    },

    // Distribute groups into 3 fluid columns to achieve the "fluid" masonry approach
    columnGroups() {
      const cols = [[], [], []];
      this.filteredGroups.forEach((group, index) => {
        cols[index % 3].push(group);
      });
      return cols;
    },

    // Return links inside manager based on category dropdown selection
    indexedLinks() {
      let allLinks = [];
      this.groups.forEach(g => {
        if (g.links) {
          g.links.forEach(l => {
            allLinks.push({
              ...l,
              parentColor: this.safeColor(g.color)
            });
          });
        }
      });

      if (this.filterManagerGroupIds.length === 0) return allLinks;
      return allLinks.filter(l => this.filterManagerGroupIds.some(gid => gid == l.group_id));
    },

    // Filtered list of links based on manager search criteria
    filteredManagerLinks() {
      let list = this.indexedLinks;
      const q = this.filterManagerSearchQuery.toLowerCase().trim();
      if (q) {
        list = list.filter(l => l.name.toLowerCase().includes(q) || l.url.toLowerCase().includes(q));
      }
      return list;
    }
  },

  mounted() {
    this.fetchData();
    
    // Install keyboard and Alt shortcuts listeners
    document.addEventListener("keydown", this.handleGlobalShortcuts);
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.handleGlobalShortcuts);
  },

  methods: {
    // -------------------------------------------------------------
    // API SYNC SERVICES
    // -------------------------------------------------------------
    async fetchData() {
      this.isLoading = true;
      try {
        const res = await this.$axios.get("/quick-links/groups");
        if (res.data && res.data.success) {
          this.groups = res.data.data || [];

        } else {
          this.$toast.error(res.data?.message || "Failed to sync Delivery Directory");
        }
      } catch (err) {
        this.$toast.error("Error communicating with Delivery Hub API.");
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    // -------------------------------------------------------------
    // ACCORDION INTERACTION CONTROLS
    // -------------------------------------------------------------
    toggleGroup(group) {
      // Toggle expanded state
      const isCurrentlyExpanded = this.isGroupExpanded(group);
      
      if (this.searchQuery.trim()) {
        // If searching, clear query or clear toggle map
        this.expandedGroupsMap[group.id] = !isCurrentlyExpanded;
      } else {
        // Single accordion selection: close others or keep open
        const newState = !this.expandedGroupsMap[group.id];
        // To behave as a strict accordion, we can reset the toggle map
        this.expandedGroupsMap = {};
        this.expandedGroupsMap[group.id] = newState;
      }
    },

    isGroupExpanded(group) {
      // If search query is active, auto-expand if it matches the query
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        const groupTitleMatch = group.title.toLowerCase().includes(q);
        const groupDescMatch = group.description && group.description.toLowerCase().includes(q);
        const linkMatch = group.links && group.links.some(l => l.name.toLowerCase().includes(q) || l.url.toLowerCase().includes(q));
        return groupTitleMatch || groupDescMatch || linkMatch;
      }
      // Otherwise, return manual toggle state
      return !!this.expandedGroupsMap[group.id];
    },

    // -------------------------------------------------------------
    // GROUP ENDPOINTS (CRUD)
    // -------------------------------------------------------------
    async saveGroup() {
      if (!this.groupForm.title || !this.groupForm.shortcut) {
        this.$toast.warning("Title and Shortcut key are both required.");
        return;
      }

      this.isLoading = true;
      try {
        const payload = {
          title: this.groupForm.title,
          description: this.groupForm.description,
          color: this.groupForm.color,
          shortcut: this.groupForm.shortcut.toUpperCase().trim()
        };

        let res;
        if (this.editGroupId) {
          res = await this.$axios.put(`/quick-links/groups/${this.editGroupId}`, payload);
        } else {
          res = await this.$axios.post("/quick-links/groups", payload);
        }

        if (res.data && res.data.success) {
          this.$toast.success(this.editGroupId ? "Grouping updated successfully!" : "New grouping category created!");
          if (this.$refs.closeGroupModal) {
            this.$refs.closeGroupModal.click();
          }
          this.cancelEditGroup();
          await this.fetchData();
        } else {
          this.$toast.error(res.data?.message || "Failed to commit grouping category");
        }
      } catch (err) {
        this.$toast.error("Network or authorization failure when saving group.");
      } finally {
        this.isLoading = false;
      }
    },

    openAddGroupModal() {
      this.cancelEditGroup();
    },

    startEditGroup(group) {
      this.editGroupId = group.id;
      this.groupForm = {
        title: group.title,
        description: group.description,
        color: this.safeColor(group.color),
        shortcut: group.shortcut || ""
      };
    },

    cancelEditGroup() {
      this.editGroupId = null;
      this.groupForm = {
        title: "",
        description: "",
        color: "blue",
        shortcut: ""
      };
    },

    async deleteGroup(group) {
      if (!confirm(`Are you absolutely sure you want to delete "${group.title}"? This will delete all associated links!`)) {
        return;
      }

      this.isLoading = true;
      try {
        const res = await this.$axios.delete(`/quick-links/groups/${group.id}`);
        if (res.data && res.data.success) {
          this.$toast.success("Grouping directory deleted successfully.");
          await this.fetchData();
        } else {
          this.$toast.error(res.data?.message || "Failed to purge grouping");
        }
      } catch (err) {
        this.$toast.error("Error purging grouping from server database.");
      } finally {
        this.isLoading = false;
      }
    },

    // -------------------------------------------------------------
    // LINK ENDPOINTS (CRUD)
    // -------------------------------------------------------------
    async saveLink() {
      if (!this.linkForm.group_id || !this.linkForm.name || !this.linkForm.url) {
        this.$toast.warning("All resource parameters are required.");
        return;
      }

      this.isLoading = true;
      try {
        const payload = {
          group_id: Number(this.linkForm.group_id),
          name: this.linkForm.name,
          url: this.linkForm.url.trim()
        };

        let res;
        if (this.editLinkId) {
          res = await this.$axios.put(`/quick-links/links/${this.editLinkId}`, payload);
        } else {
          res = await this.$axios.post("/quick-links/links", payload);
        }

        if (res.data && res.data.success) {
          this.$toast.success(this.editLinkId ? "Resource details updated successfully!" : "New link saved to delivery directory!");
          if (this.$refs.closeLinkModal) {
            this.$refs.closeLinkModal.click();
          }
          this.cancelEditLink();
          await this.fetchData();
        } else {
          this.$toast.error(res.data?.message || "Failed to commit link details");
        }
      } catch (err) {
        this.$toast.error("Network or authorization failure when committing link details.");
      } finally {
        this.isLoading = false;
      }
    },

    openAddLinkModal() {
      this.cancelEditLink();
    },

    startEditLink(link) {
      this.editLinkId = link.id;
      this.linkForm = {
        group_id: link.group_id,
        name: link.name,
        url: link.url
      };
    },

    cancelEditLink() {
      this.editLinkId = null;
      this.linkForm = {
        group_id: "",
        name: "",
        url: ""
      };
    },

    async deleteLink(link) {
      if (!confirm(`Remove "${link.name}" from directory?`)) {
        return;
      }

      this.isLoading = true;
      try {
        const res = await this.$axios.delete(`/quick-links/links/${link.id}`);
        if (res.data && res.data.success) {
          this.$toast.success("Resource link deleted successfully.");
          await this.fetchData();
        } else {
          this.$toast.error(res.data?.message || "Failed to purge link");
        }
      } catch (err) {
        this.$toast.error("Error purging link from server database.");
      } finally {
        this.isLoading = false;
      }
    },

    // -------------------------------------------------------------
    // HELPER FUNCTIONS & KEYBOARD SHORTCUTS
    // -------------------------------------------------------------
    safeColor(color) {
      if (!color || color === "white") return "blue";
      const exists = this.colorThemesList.some(t => t.value === color);
      return exists ? color : "blue";
    },

    getColorThemeLabel(color) {
      const sanitized = this.safeColor(color);
      const theme = this.colorThemesList.find(t => t.value === sanitized);
      return theme ? theme.label : "Select Theme...";
    },

    getGroupTitleById(id) {
      const g = this.groups.find(x => x.id === Number(id));
      return g ? g.title : "";
    },

    truncateUrl(url) {
      if (!url) return "";
      try {
        const u = new URL(url);
        return u.hostname + (u.pathname && u.pathname !== "/" ? u.pathname : "");
      } catch (e) {
        return url;
      }
    },

    getGroupTitle(groupId) {
      const g = this.groups.find(x => x.id === groupId);
      return g ? g.title : "Unassigned";
    },

    isSearchMatch(name) {
      const q = this.searchQuery.toLowerCase().trim();
      if (!q) return false;
      return name.toLowerCase().includes(q);
    },

    handleGlobalShortcuts(event) {
      // Ignore shortcuts if typing in any text field or input
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")) {
        return;
      }

      // ALT + Shortcut Key combination triggers the respective category expansion and scrolling
      if (event.altKey && event.key) {
        const k = event.key.toUpperCase();
        const matchingGroup = this.groups.find(g => g.shortcut === k);
        if (matchingGroup) {
          event.preventDefault();

          // Smoothly snap tab view back to Hub if they are currently inside Settings
          this.activeTab = "hub";
          
          // If the group is already expanded, close it. Otherwise, expand it and close all others.
          const isCurrentlyExpanded = !!this.expandedGroupsMap[matchingGroup.id];
          
          this.expandedGroupsMap = {};
          if (!isCurrentlyExpanded) {
            this.expandedGroupsMap[matchingGroup.id] = true;
          }
          this.expandedGroupsMap = { ...this.expandedGroupsMap };
          
          // Scroll smoothly to the expanded card block
          this.$nextTick(() => {
            const cardEl = document.getElementById(`accordion-group-${matchingGroup.id}`);
            if (cardEl) {
              cardEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          });
        }
      }
    }
  }
};
</script>

<style scoped>
/* -------------------------------------------------------------
   PREMIUM LIGHT THEME CSS UTILITIES (Matches Spark Layout)
   ------------------------------------------------------------- */
.quick-links-page {
  font-family: "Inter", "Outfit", -apple-system, sans-serif;
  color: #435666; /* Standard Slate text */
}

/* Light Theme Variables */
.bg-light-card-inner {
  background: #f8fafc; /* Slate 50 */
}
.bg-light-inset {
  background: #f1f5f9; /* Slate 100 */
}
.border-secondary-light {
  border-color: #e2e8f0 !important; /* Slate 200 */
}
.text-muted-custom {
  color: #64748b !important; /* Slate 500 */
}
.text-light-custom {
  color: #334155 !important; /* Slate 700 */
}
.text-xxs {
  font-size: 0.7rem;
}

/* Hero Title Gradient (Premium Blues/Teals) */
.text-gradient {
  background: linear-gradient(135deg, #0284c7 0%, #4f46e5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* -------------------------------------------------------------
   SPARK UNIFIED HEADERS & BREADCRUMBS (Matches Spark's Premium Card Header)
   ------------------------------------------------------------- */
.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}
.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ms-page-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 130, 152, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: #008298;
}
.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}
.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.3);
}
.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

/* -------------------------------------------------------------
   ORGANISATION DASHBOARD NAV TABS BINDINGS
   ------------------------------------------------------------- */
.nav-tabs {
  border-bottom: 1px solid #e2e8f0 !important;
}
.nav-tabs .nav-link {
  color: #64748b;
  font-size: 0.92rem;
  border: none;
  background: none;
  border-bottom: 3px solid transparent;
  transition: all 0.15s ease;
  margin-bottom: -1px;
}
.nav-tabs .nav-link:hover {
  color: #1e293b;
  border-bottom: 3px solid #cbd5e1;
}
.nav-tabs .nav-link.active {
  color: #0dcaf0 !important;
  border-bottom: 3px solid #0dcaf0 !important;
  font-weight: 800;
}

/* -------------------------------------------------------------
   REGRESSION COMPATIBLE FLUID STACK CARDS
   ------------------------------------------------------------- */
.search-wrapper .search-icon {
  position: absolute;
  top: 50%;
  left: 1.25rem;
  transform: translateY(-50%);
  font-size: 1.2rem;
}
.focus-glow:focus {
  border-color: #38bdf8 !important;
  box-shadow: 0 0 0 0.25rem rgba(56, 189, 248, 0.15) !important;
}

/* Custom styled project-card directly from regression library dashboard */
.project-card {
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.22s ease, border-color 0.22s ease;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  background: #ffffff;
}
.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(67, 86, 102, 0.08) !important;
}

/* Hover highlights for selected card */
.project-card.is-expanded-card {
  border-color: var(--theme-color) !important;
  box-shadow: 0 10px 25px var(--theme-color-shadow) !important;
}

/* Icon Box matching design of Regression Library */
.project-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  transition: transform 0.15s ease;
}
.project-card:hover .project-icon-wrapper {
  transform: scale(1.05);
}

/* Project card title */
.card-project-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
  transition: color 0.15s ease;
}
.project-card:hover .card-project-title {
  color: var(--theme-color);
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Footer Link Action layout matching Regression Library */
.card-action-link {
  transition: color 0.15s ease;
}
.chevron-spin {
  transition: transform 0.2s ease;
}
.chevron-spin.rotated {
  transform: rotate(180deg);
  color: var(--theme-color) !important;
}

/* Rounded Theme-Color definitions for Icon Wrappers and Card highlights */
.blue-theme { background-color: rgba(2, 132, 199, 0.08); color: #0284c7; }
.green-theme { background-color: rgba(16, 185, 129, 0.08); color: #10b981; }
.amber-theme { background-color: rgba(245, 158, 11, 0.08); color: #f59e0b; }
.cyan-theme { background-color: rgba(6, 182, 212, 0.08); color: #06b6d4; }
.red-theme { background-color: rgba(239, 68, 68, 0.08); color: #ef4444; }
.purple-theme { background-color: rgba(139, 92, 246, 0.08); color: #8b5cf6; }
.pink-theme { background-color: rgba(236, 72, 153, 0.08); color: #ec4899; }
.lime-theme { background-color: rgba(132, 204, 22, 0.08); color: #84cc16; }
.teal-theme { background-color: rgba(20, 184, 166, 0.08); color: #14b8a6; }
.indigo-theme { background-color: rgba(99, 102, 241, 0.08); color: #6366f1; }
.orange-theme { background-color: rgba(249, 115, 22, 0.08); color: #f97316; }
.emerald-theme { background-color: rgba(16, 185, 129, 0.08); color: #10b981; }
.rose-theme { background-color: rgba(244, 63, 94, 0.08); color: #f43f5e; }
.violet-theme { background-color: rgba(139, 92, 246, 0.08); color: #8b5cf6; }
.slate-theme { background-color: rgba(71, 85, 105, 0.08); color: #475569; }
.gray-theme { background-color: rgba(100, 116, 139, 0.08); color: #64748b; }

.theme-color-blue { --theme-color: #0284c7; --theme-color-shadow: rgba(2, 132, 199, 0.1); }
.theme-color-green { --theme-color: #10b981; --theme-color-shadow: rgba(16, 185, 129, 0.1); }
.theme-color-amber { --theme-color: #f59e0b; --theme-color-shadow: rgba(245, 158, 11, 0.1); }
.theme-color-cyan { --theme-color: #06b6d4; --theme-color-shadow: rgba(6, 182, 212, 0.1); }
.theme-color-red { --theme-color: #ef4444; --theme-color-shadow: rgba(239, 68, 68, 0.1); }
.theme-color-purple { --theme-color: #8b5cf6; --theme-color-shadow: rgba(139, 92, 246, 0.1); }
.theme-color-pink { --theme-color: #ec4899; --theme-color-shadow: rgba(236, 72, 153, 0.1); }
.theme-color-lime { --theme-color: #84cc16; --theme-color-shadow: rgba(132, 204, 22, 0.1); }
.theme-color-teal { --theme-color: #14b8a6; --theme-color-shadow: rgba(20, 184, 166, 0.1); }
.theme-color-indigo { --theme-color: #6366f1; --theme-color-shadow: rgba(99, 102, 241, 0.1); }
.theme-color-orange { --theme-color: #f97316; --theme-color-shadow: rgba(249, 115, 22, 0.1); }
.theme-color-emerald { --theme-color: #10b981; --theme-color-shadow: rgba(16, 185, 129, 0.1); }
.theme-color-rose { --theme-color: #f43f5e; --theme-color-shadow: rgba(244, 63, 94, 0.1); }
.theme-color-violet { --theme-color: #8b5cf6; --theme-color-shadow: rgba(139, 92, 246, 0.1); }
.theme-color-slate { --theme-color: #475569; --theme-color-shadow: rgba(71, 85, 105, 0.1); }
.theme-color-gray { --theme-color: #64748b; --theme-color-shadow: rgba(100, 116, 139, 0.1); }

.text-color-label {
  color: var(--theme-color) !important;
}
.bg-color {
  background-color: var(--theme-color) !important;
}

/* -------------------------------------------------------------
   PURE CLEAN WHITE LINK LIST (NO GREYISH BACKGROUNDS)
   ------------------------------------------------------------- */
.border-light-subtle {
  border-color: #f1f5f9 !important; /* Extremely soft grey line separator */
}

.link-item-row {
  padding: 10px 14px !important;
  border-radius: 8px !important;
  background-color: #fcfdfe !important;
  border: 1px solid rgba(0, 0, 0, 0.03) !important;
  border-left: 3px solid transparent !important;
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.link-item-row:hover {
  background-color: #f8fafc !important;
  border-color: rgba(0, 0, 0, 0.06) !important;
  border-left-color: var(--theme-color, #0ea5e9) !important;
  transform: translateX(4px);
}
.link-item-row:hover .arrow-reveal {
  color: var(--theme-color) !important;
  transform: translate(2px, -2px);
}
.arrow-reveal {
  transition: transform 0.15s ease, color 0.15s ease;
}

.bullet-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  display: inline-block;
}

.hover-underline:hover {
  text-decoration: underline !important;
}

/* Spacious, padded, beautiful left-accented pill highlight box for matching search items */
.glow-highlight {
  background: rgba(56, 189, 248, 0.04) !important;
  border-left: 3px solid var(--theme-color) !important;
  border-radius: 8px !important;
  padding-left: 12px !important;
  margin: 4px 0 !important;
  border-bottom: 1px solid transparent !important;
  animation: pulseHighlight 2s infinite alternate;
}

@keyframes pulseHighlight {
  from { box-shadow: 0 0 3px rgba(56, 189, 248, 0.05); }
  to { box-shadow: 0 0 8px rgba(56, 189, 248, 0.15); }
}

/* Height slide transitions for smooth native accordion body toggles */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
  max-height: 1000px;
  overflow: hidden;
}
.slide-fade-enter, .slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Slide in animations for tabs content */
.fade-in-content {
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* -------------------------------------------------------------
   SETTINGS & MANAGEMENT STYLES
   ------------------------------------------------------------- */
.rounded-xl {
  border-radius: 12px !important;
}
.p-3.5 {
  padding: 0.85rem !important;
}
.uppercase-tracker {
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.uppercase-input {
  text-transform: uppercase;
}

.hover-card {
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.hover-card:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.group-row, .link-row {
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  border-left: 4px solid var(--theme-color, #0ea5e9) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.01) !important;
  background: #ffffff;
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.group-row:hover, .link-row:hover {
  background: #fcfdfe !important;
  border-color: rgba(0, 0, 0, 0.06) !important;
  border-left: 4px solid var(--theme-color, #0ea5e9) !important;
  transform: translateX(4px);
  box-shadow: 0 8px 20px rgba(67, 86, 102, 0.06) !important;
}

.group-shortcut-badge {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Outfit", "Inter", sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  transition: all 0.2s ease;
}

.action-btn-edit, .action-btn-delete {
  width: 32px;
  height: 32px;
  border-radius: 50% !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  background-color: transparent;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
}

.action-btn-edit {
  color: #00A0C1 !important;
  border-color: rgba(0, 160, 193, 0.2) !important;
}

.action-btn-edit:hover {
  background-color: rgba(0, 160, 193, 0.08) !important;
  border-color: #00A0C1 !important;
  transform: scale(1.05);
}

.action-btn-delete {
  color: #ef4444 !important;
  border-color: rgba(239, 68, 68, 0.2) !important;
}

.action-btn-delete:hover {
  background-color: rgba(239, 68, 68, 0.08) !important;
  border-color: #ef4444 !important;
  transform: scale(1.05);
}

.gap-1\.5 {
  gap: 0.375rem !important;
}
.gap-2\.5 {
  gap: 0.625rem !important;
}

.btn-xs {
  padding: 0.2rem 0.4rem;
  font-size: 0.7rem;
  border-radius: 4px;
}
.p-2.5 {
  padding: 0.6rem !important;
}
.p-1 {
  padding: 0.25rem 0.4rem !important;
}
.text-xxs {
  font-size: 0.65rem !important;
}

/* Custom Dropdown select matching Vertex style */
.custom-select-dropdown .dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0.35em solid;
  border-right: 0.35em solid transparent;
  border-bottom: 0;
  border-left: 0.35em solid transparent;
}
.custom-select-dropdown .dropdown-menu {
  box-shadow: 0 10px 25px rgba(67, 86, 102, 0.1) !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  z-index: 1060;
}
.custom-select-dropdown .dropdown-item {
  transition: background-color 0.1s ease;
}
.custom-select-dropdown .dropdown-item:hover {
  background-color: #f1f5f9;
}
.scale-x-n1 {
  transform: scaleX(-1);
}

/* Custom filter dropdown button */
.custom-filter-dropdown-btn {
   background-color: #fff !important;
   border: 1px solid #dee2e6 !important;
   color: #495057 !important;
   transition: all 0.2s ease;
}
.custom-filter-dropdown-btn:hover,
.custom-filter-dropdown-btn:focus,
.custom-filter-dropdown-btn[aria-expanded="true"] {
   border-color: #00A0C1 !important;
   background-color: #f8f9fa !important;
   color: #00A0C1 !important;
}

/* Add Grouping custom action button styles */
.btn-directory-action {
  background-color: #ffffff !important;
  border-color: rgba(13, 202, 240, 0.3) !important;
  color: #0dcaf0 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.btn-directory-action:hover, .btn-directory-action:focus {
  background: linear-gradient(135deg, #0dcaf0, #0bacce) !important;
  border-color: #0bacce !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(13, 202, 240, 0.15) !important;
}

/* Ultra-slim modern scrollbars */
.groups-list::-webkit-scrollbar,
.links-manager-list::-webkit-scrollbar {
  width: 5px !important;
  height: 5px !important;
}
.groups-list::-webkit-scrollbar-track,
.links-manager-list::-webkit-scrollbar-track {
  background: transparent !important;
}
.groups-list::-webkit-scrollbar-thumb,
.links-manager-list::-webkit-scrollbar-thumb {
  background: #cbd5e1 !important;
  border-radius: 10px !important;
}
.groups-list::-webkit-scrollbar-thumb:hover,
.links-manager-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8 !important;
}

/* Dynamic Theme Badge/Bullet Classes */
.bg-blue { background-color: #0ea5e9 !important; }
.text-blue { color: #0ea5e9 !important; }
.border-blue { border-color: rgba(14, 165, 233, 0.2) !important; background-color: rgba(14, 165, 233, 0.05) !important; }

.bg-green { background-color: #10b981 !important; }
.text-green { color: #10b981 !important; }
.border-green { border-color: rgba(16, 185, 129, 0.2) !important; background-color: rgba(16, 185, 129, 0.05) !important; }

.bg-amber { background-color: #f59e0b !important; }
.text-amber { color: #f59e0b !important; }
.border-amber { border-color: rgba(245, 158, 11, 0.2) !important; background-color: rgba(245, 158, 11, 0.05) !important; }

.bg-cyan { background-color: #06b6d4 !important; }
.text-cyan { color: #06b6d4 !important; }
.border-cyan { border-color: rgba(6, 182, 212, 0.2) !important; background-color: rgba(6, 182, 212, 0.05) !important; }

.bg-red { background-color: #ef4444 !important; }
.text-red { color: #ef4444 !important; }
.border-red { border-color: rgba(239, 68, 68, 0.2) !important; background-color: rgba(239, 68, 68, 0.05) !important; }

.bg-purple { background-color: #8b5cf6 !important; }
.text-purple { color: #8b5cf6 !important; }
.border-purple { border-color: rgba(139, 92, 246, 0.2) !important; background-color: rgba(139, 92, 246, 0.05) !important; }

.bg-pink { background-color: #ec4899 !important; }
.text-pink { color: #ec4899 !important; }
.border-pink { border-color: rgba(236, 72, 153, 0.2) !important; background-color: rgba(236, 72, 153, 0.05) !important; }

.bg-lime { background-color: #84cc16 !important; }
.text-lime { color: #84cc16 !important; }
.border-lime { border-color: rgba(132, 204, 22, 0.2) !important; background-color: rgba(132, 204, 22, 0.05) !important; }

.bg-teal { background-color: #14b8a6 !important; }
.text-teal { color: #14b8a6 !important; }
.border-teal { border-color: rgba(20, 184, 166, 0.2) !important; background-color: rgba(20, 184, 166, 0.05) !important; }

.bg-indigo { background-color: #6366f1 !important; }
.text-indigo { color: #6366f1 !important; }
.border-indigo { border-color: rgba(99, 102, 241, 0.2) !important; background-color: rgba(99, 102, 241, 0.05) !important; }

.bg-orange { background-color: #f97316 !important; }
.text-orange { color: #f97316 !important; }
.border-orange { border-color: rgba(249, 115, 22, 0.2) !important; background-color: rgba(249, 115, 22, 0.05) !important; }

.bg-emerald { background-color: #10b981 !important; }
.text-emerald { color: #10b981 !important; }
.border-emerald { border-color: rgba(16, 185, 129, 0.2) !important; background-color: rgba(16, 185, 129, 0.05) !important; }

.bg-rose { background-color: #f43f5e !important; }
.text-rose { color: #f43f5e !important; }
.border-rose { border-color: rgba(244, 63, 94, 0.2) !important; background-color: rgba(244, 63, 94, 0.05) !important; }

.bg-violet { background-color: #8b5cf6 !important; }
.text-violet { color: #8b5cf6 !important; }
.border-violet { border-color: rgba(139, 92, 246, 0.2) !important; background-color: rgba(139, 92, 246, 0.05) !important; }

.bg-slate { background-color: #475569 !important; }
.text-slate { color: #475569 !important; }
.border-slate { border-color: rgba(71, 85, 105, 0.2) !important; background-color: rgba(71, 85, 105, 0.05) !important; }

.bg-gray { background-color: #64748b !important; }
.text-gray { color: #64748b !important; }
.border-gray { border-color: rgba(100, 116, 139, 0.2) !important; background-color: rgba(100, 116, 139, 0.05) !important; }

/* Custom checkbox input style directly from Leads page */
.custom-checkbox-input {
   display: none !important;
}
.custom-checkbox-input + .custom-checkbox {
   width: 18px !important;
   height: 18px !important;
   border: 2px solid #dee2e6 !important;
   border-radius: 4px !important;
   display: inline-block !important;
   position: relative !important;
   cursor: pointer !important;
   transition: all 0.2s ease !important;
   background-color: #fff !important;
}
.custom-checkbox-input:checked + .custom-checkbox {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}
.custom-checkbox-input:checked + .custom-checkbox::after {
   content: '\f00c' !important;
   font-family: 'Font Awesome 6 Free' !important;
   font-weight: 900 !important;
   font-size: 10px !important;
   color: #fff !important;
   position: absolute !important;
   top: 50% !important;
   left: 50% !important;
   transform: translate(-50%, -50%) !important;
}
.select-none {
  user-select: none !important;
}
.thin-scrollbar-dropdown::-webkit-scrollbar {
  width: 4px !important;
}
.thin-scrollbar-dropdown::-webkit-scrollbar-track {
  background: transparent !important;
}
.thin-scrollbar-dropdown::-webkit-scrollbar-thumb {
  background: #cbd5e1 !important;
  border-radius: 4px !important;
}
.thin-scrollbar-dropdown::-webkit-scrollbar-thumb:hover {
  background: #94a3b8 !important;
}
/* -------------------------------------------------------------
   BDM DETAILS STANDARD MODAL OVERRIDES
   ------------------------------------------------------------- */
.modal-title-custom {
   font-weight: 700 !important;
   font-size: 1.2rem !important;
   text-align: left !important;
}

.close-button-custom {
   position: absolute !important;
   top: 1.25rem !important;
   right: 1.5rem !important;
   font-size: 1.85rem !important;
   font-weight: 700 !important;
   line-height: 1 !important;
   color: #94a3b8 !important;
   background: none !important;
   border: none !important;
   cursor: pointer !important;
   transition: color 0.15s ease !important;
   padding: 0 !important;
}

.close-button-custom:hover {
   color: #1e293b !important;
}

.modal-form-custom .form-label {
   font-weight: 700 !important;
   font-size: 11px !important;
   text-transform: uppercase !important;
   letter-spacing: 0.05em !important;
   color: #475569 !important;
   margin-bottom: 6px !important;
}

.modal-form-custom .form-control {
  font-size: 14px !important;
  padding: 10px 14px !important;
  border-radius: 8px !important;
  color: #1e293b !important;
  border-color: #dee2e6 !important;
  background-color: #ffffff !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
}

.modal-form-custom .form-control::placeholder {
  color: #94a3b8 !important;
}

.modal-form-custom .form-control:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15) !important;
}

/* Specify uniform height for standard text inputs */
.modal-form-custom input.form-control {
  height: 42px !important;
}

/* Prevent fixed height on textareas so they scale naturally with rows */
.modal-form-custom textarea.form-control {
  height: auto !important;
}

.modal-custom-width {
  max-width: 540px !important;
}
</style>
